import getEnvironment from './getEnvironment';
import { parseQueryParams } from './queryParams';
import { get } from 'lodash';
import { stringify } from 'query-string';
import * as moment from 'moment';
import Reward from './types/Reward';

const { env } = parseQueryParams(window.location.search);

if (!env) {
  throw new Error('Please use an `env` parameter.');
}

const { baseUrl } = getEnvironment(env).tvsmiles;

const urls = {
  details: `${baseUrl}/api/v6/users/details`,
  statistics: `${baseUrl}/api/v6/users/statistics`,
  vipPoints: `${baseUrl}/api/v6/users/insights/20210201_CURRENT_VIP_POINTS`,
  oldPoints: `${baseUrl}/api/v6/users/insights/20210201_OLD_POINTS`,
  ttrans: `${baseUrl}/api/v6/users/insights/wishlist`,
};

interface ICache {
  [url: string]: Promise<{}>;
}

function saveUserInsight(baseURL: string, deviceUDID: string, key: string, value: any)
    : Promise<void> {
  let savedValue = typeof value === 'undefined' ? null : value;
  if (typeof value === 'object') {
    savedValue = JSON.stringify(value);
  }
  const body = [{ key, value: savedValue }];
  const options = {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      Accept: 'application/json',
      Authorization: `Basic ${btoa(`${deviceUDID}:`)}`,
      'Content-Type': 'application/json',
    },
  };
  return fetch(`${baseURL}/api/v5/user/insight`, options)
      .then(r => r.json());
}

const cachedFetchPromises: ICache = {};

const BackendAPI = {
  cachedFetchPromises,

  // Returns a promise with data from a given backend URL. The promise is cached and reused to
  // make maximally one request per session.
  fetchDataOnce(url: string, init?: RequestInit): Promise<any> {
    if (this.cachedFetchPromises[url]) {
      return this.cachedFetchPromises[url];
    }
    this.cachedFetchPromises[url] = fetch(url, init)
      .then(response => response.json())
      .then(json => Object.freeze(json));
    return this.cachedFetchPromises[url];
  },

  fetchData(url: string, init?: RequestInit): Promise<any> {
    return fetch(url, init)
      .then((response) => {
        if (!response.ok) {
          throw new Error(get(response, 'error.message') || 'Could not fetch data from backend.');
        }
        return response;
      })
      .then(response => response.json())
      .then(json => Object.freeze(json));
  },

  // Saves data to the backend as JSON, returning the JSON response wrapped in a Promise.
  saveData(url: string, body: {}) {
    return fetch(url, {
      body: JSON.stringify(body),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'post',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error on ${url}.`);
        }
        return response;
      });
  },

  createRewardPurchaseNotShop({ env, rewardId, rewards, deviceUDID } : {
    env: string,
    rewardId: string,
    rewards: Reward[],
    deviceUDID: string,
  }) : Promise<void> {
    const selectedReward = rewards.find(r => r.id === rewardId)!;

    const amount = selectedReward.amount;
    const vipAmount = selectedReward.vipAmount;
    const orderTimestamp = moment.now();
    const query = stringify({ env, amount, vipAmount, rewardId, deviceUDID, orderTimestamp });
    return saveUserInsight(baseUrl, deviceUDID, 'wishlist', query);
  },

  fetchDetails(idToken: string, profile?: { [key: string]: string }): Promise<any> {
    const init: RequestInit = {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    };
    if (profile) {
      init.method = 'POST';
      init.body = JSON.stringify(profile);
    }
    return this.fetchData(urls.details, init);
  },

  fetchStatistics(idToken: string): Promise<any> {
    return this.fetchData(urls.statistics, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  },

  fetchVipPoints(idToken: string): Promise<any> {
    return this.fetchData(urls.vipPoints, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  },

  fetchOldPoints(idToken: string): Promise<any> {
    return this.fetchData(urls.oldPoints, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  },

  fetchCanRedeemToday(idToken: string): Promise<any> {
    return this.fetchData(urls.ttrans, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  },

  clearCache() {
    this.cachedFetchPromises = {};
  },
};

export default BackendAPI;



// WEBPACK FOOTER //
// ./src/lib/BackendAPI.ts